import React, { CSSProperties, useMemo } from 'react'
import { MultiSelect } from '@/components/Select'
import { FilterDescriptor } from '@progress/kendo-data-query'
import { isEmpty } from 'utils/is-empty'

type SelectValue = string | number
type FilterSelectedValue = SelectValue | { id?: SelectValue | null; value?: SelectValue | null }

interface FilterValue {
  id: SelectValue
  label: string | number | null
  value: SelectValue
}

interface MultiSelectFilterProps {
  placeholder?: string
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
  data: FilterValue[]
  style?: CSSProperties
}

const isSelectValue = (value: unknown): value is SelectValue =>
  typeof value === 'number' || (typeof value === 'string' && value !== '')

const getFilterSelectedValue = (value: FilterSelectedValue): SelectValue | undefined => {
  if (isSelectValue(value)) return value

  if (value && typeof value === 'object') {
    if (isSelectValue(value.value)) return value.value
    if (isSelectValue(value.id)) return value.id
  }

  return undefined
}

const getSelectedValues = (value: unknown): SelectValue[] => {
  const values = Array.isArray(value) ? value : value == null ? [] : [value]

  return values
    .map((item) => getFilterSelectedValue(item as FilterSelectedValue))
    .filter((item): item is SelectValue => item !== undefined)
}

export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  placeholder,
  filter,
  onFilterChange,
  data,
  style,
}) => {
  const options = useMemo(
    () =>
      data.map((item) => {
        const label = String(item.label ?? item.id)

        return {
          key: item.id,
          value: item.value,
          label,
          text: label,
        }
      }),
    [data]
  )
  const selectedValues = useMemo(() => getSelectedValues(filter.value), [filter.value])

  return (
    <div style={{ width: 250, ...style }}>
      <MultiSelect
        name={typeof filter.field === 'string' ? filter.field : undefined}
        placeholder={placeholder}
        options={options}
        value={selectedValues}
        fullWidth
        showSearch
        onChange={(value) => {
          const nextValue = getSelectedValues(value)

          if (isEmpty(nextValue)) {
            onFilterChange.call(undefined, {
              nextFilter: { ...filter, value: null },
            })
            return
          }

          onFilterChange.call(undefined, {
            nextFilter: { ...filter, value: nextValue },
          })
        }}
      />
    </div>
  )
}
